import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Card, CardBody, Col, Container, Row, Spinner, Button, Input,
} from "reactstrap";
import ParticlesAuth from "../Authentication/ParticlesAuth";
import logo_farmabien from "../../assets/images/logo-farme.png";
import logo_mercantil from "../../assets/images/logo-mercantil.png";
import { getSolicitudQR, resetSolicitudFlag } from "../../slices/thunks";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { getUrlPrefirmadaPublic, enviarFeedbackQRPublic } from "../../helpers/backend_helper";
import FileViewer from "../../Components/Common/FileViewer";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as moment from "moment";

const FEEDBACK_TAGS = [
   "Entrega tardía",
    "Medicamento no disponible",
    "Mala atención",
    "Precio incorrecto",
    "Otro"
];

const handleValidDate = (date) => moment(new Date(date)).format("DD MMM Y");

const VerificarFactura = () => {
  const dispatch = useDispatch();
  const { id, factura } = useParams();
  const [adjuntoUrl, setAdjuntoUrl] = useState(null);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState({ url: null, name: "", type: "" });

  useEffect(() => {
    return () => { dispatch(resetSolicitudFlag()); };
  }, [dispatch]);

  useEffect(() => {
    dispatch(getSolicitudQR({ id, factura }));
  }, [dispatch, factura, id]);

  const pageData = createSelector(
    (state) => state,
    (state) => ({
      facturacionQR: state.Solicitudes.facturacionQR,
      error: state.Solicitudes.error,
      loading: state.Solicitudes.loading,
      errorMsg: state.Solicitudes.errorMsg,
      colorMsg: state.Solicitudes.colorMsg,
      exito: state.Solicitudes.exito,
    })
  );

  const { facturacionQR, error, loading, errorMsg } = useSelector(pageData);

  // ─── Feedback QR state ───
  const [rating, setRating] = useState(null);
  const [feedbackTags, setFeedbackTags] = useState([]);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const aseguradora = facturacionQR?.aseguradora || "MyH+";
  const esMercantil = aseguradora === "Seguros Mercantil";

  // Normalizar nombre de farmacia (misma regla que qr_email.py)
  const rawFarmacia = (facturacionQR?.nombreFarmacia || "").trim();
  const nombreFarmaciaNormalizado = ["merida", "mérida", "caracas"].includes(rawFarmacia.toLowerCase())
    ? "FarmaBien 8B C.A."
    : rawFarmacia || "Farme";
  const feedbackEnabled = useMemo(
    () => Boolean(facturacionQR) && aseguradora !== "MyH+",
    [facturacionQR, aseguradora]
  );
  const feedbackAlreadySent = Boolean(facturacionQR?.feedbackEnviado) || feedbackSubmitted;
  const isLowRating = rating !== null && rating <= 3;
  // Ocultar pills si el usuario envió feedback negativo, para reducir ruido
  const hideSecondaryPills = feedbackSubmitted && isLowRating;

  const toggleTag = (tag) => {
    setFeedbackTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const submitFeedback = async () => {
    if (!rating) return;
    try {
      setSubmittingFeedback(true);
      await enviarFeedbackQRPublic(id, factura, {
        valoracion: rating,
        tags: rating <= 3 ? feedbackTags : [],
        comentarioAdicional: feedbackComment.trim() || null,
      });
      setFeedbackSubmitted(true);
      toast.success("¡Gracias por tu feedback!");
    } catch (err) {
      const code = err?.response?.status;
      if (code === 409) {
        setFeedbackSubmitted(true);
        toast.info("Ya habíamos recibido tu feedback. ¡Gracias!");
      } else if (code === 403) {
        toast.error("Esta solicitud no admite feedback público.");
      } else {
        toast.error("No pudimos registrar tu feedback. Intenta nuevamente.");
      }
    } finally {
      setSubmittingFeedback(false);
    }
  };

  useEffect(() => {
    if (facturacionQR && facturacionQR.detalles && facturacionQR.detalles.adjunto) {
      if (typeof facturacionQR.detalles.adjunto === "string" &&
          facturacionQR.detalles.adjunto.startsWith("http") &&
          !facturacionQR.detalles.adjunto.startsWith("data:")) {
        getUrlPrefirmadaPublic(facturacionQR.detalles.adjunto)
          .then((response) => {
            if (response.data && response.data.url) {
              setAdjuntoUrl(response.data.url);
            } else {
              setAdjuntoUrl(null);
            }
          })
          .catch(() => { setAdjuntoUrl(null); });
      } else {
        setAdjuntoUrl(facturacionQR.detalles.adjunto);
      }
    }
  }, [facturacionQR]);

  const isImageFile = (fileData) => {
    if (!fileData) return false;
    if (fileData.startsWith("http")) {
      const ext = fileData.split("?")[0].toLowerCase();
      return ext.endsWith(".jpg") || ext.endsWith(".jpeg") || ext.endsWith(".png") || ext.endsWith(".gif") || ext.endsWith(".webp") || fileData.includes("/image/");
    }
    return fileData.includes("data:image/") || (fileData.includes("base64") && !fileData.includes("JVBERi0"));
  };

  const isPdfFile = (fileData) => {
    if (!fileData) return false;
    if (fileData.startsWith("http")) {
      const ext = fileData.split("?")[0].toLowerCase();
      return ext.endsWith(".pdf") || fileData.includes("/pdf/") || fileData.includes("content-type=application%2Fpdf");
    }
    return fileData.includes("data:application/pdf") || fileData.includes("JVBERi0");
  };

  const openAdjunto = () => {
    if (!adjuntoUrl) { toast.error("No hay comprobante disponible"); return; }
    if (adjuntoUrl.startsWith("http") && !adjuntoUrl.includes("X-Amz-Signature")) {
      getUrlPrefirmadaPublic(adjuntoUrl)
        .then((response) => {
          if (response.data && response.data.url) {
            const fileType = isPdfFile(adjuntoUrl) ? "pdf" : isImageFile(adjuntoUrl) ? "image" : "other";
            setCurrentFile({ url: response.data.url, name: "Factura", type: fileType });
            setFileViewerOpen(true);
          } else { toast.error("No se pudo obtener la URL prefirmada"); }
        })
        .catch(() => { toast.error("Error al obtener la URL prefirmada"); });
    } else {
      const fileType = isPdfFile(adjuntoUrl) ? "pdf" : isImageFile(adjuntoUrl) ? "image" : "other";
      setCurrentFile({ url: adjuntoUrl, name: "Factura", type: fileType });
      setFileViewerOpen(true);
    }
  };

  // ─── STYLES ───
  const styles = {
    wrapper: { paddingTop: "0.5rem", paddingBottom: "2rem", minHeight: "100vh" },
    whiteBackdrop: { position: "fixed", inset: 0, backgroundColor: "#ffffff", zIndex: 0 },
    card: { borderRadius: "16px", border: "1px solid #e5e7eb" },
    headerError: { background: "linear-gradient(135deg, #f06548 0%, #d9534f 100%)", padding: "2rem 1.5rem" },
    iconCircle: { width: 56, height: 56, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center" },
    successCircle: { width: 64, height: 64, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: "#0ab39c", boxShadow: "0 6px 18px rgba(10,179,156,0.35)" },
    receiptCard: { border: "1px solid #e5e7eb", borderRadius: "14px", padding: "1rem 1.25rem", backgroundColor: "#fff" },
    receiptHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" },
    receiptHeaderLabel: { fontSize: "0.7rem", fontWeight: 600, color: "#878a99", textTransform: "uppercase", letterSpacing: "0.6px" },
    verifiedBadge: { fontSize: "0.7rem", fontWeight: 700, color: "#065f46", backgroundColor: "#d1fae5", padding: "0.25rem 0.6rem", borderRadius: "999px", letterSpacing: "0.5px" },
    receiptRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 0", borderBottom: "1px dashed #e5e7eb", gap: "0.75rem" },
    receiptRowLast: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 0", gap: "0.75rem" },
    receiptLabel: { fontSize: "0.8rem", color: "#6b7280", margin: 0 },
    receiptValue: { fontSize: "0.9rem", fontWeight: 600, color: "#111827", margin: 0, textAlign: "right", wordBreak: "break-word" },
    footer: { fontSize: "0.75rem", color: "#6b7280", marginTop: "1.5rem", textAlign: "center" },
    mercantilFooter: { fontSize: "0.8rem", color: "#6b7280", marginTop: "2rem", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" },
  };

  // ─── LOADING ───
  if (loading || (!facturacionQR && !error)) {
    return (
      <ParticlesAuth>
        <div className="auth-page-content d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
          <div className="text-center">
            <div className="bg-white d-inline-block p-3 rounded-circle shadow mb-3">
              <Spinner color="primary" style={{ width: "2.5rem", height: "2.5rem" }} />
            </div>
            <h5 className="text-white mt-2">Verificando su solicitud...</h5>
            <p className="text-white-50 small">Estamos validando la información de su factura</p>
          </div>
        </div>
      </ParticlesAuth>
    );
  }

  // ─── MAIN RENDER ───
  return (
    <ParticlesAuth>
      <FileViewer
        isOpen={fileViewerOpen}
        toggle={() => setFileViewerOpen(!fileViewerOpen)}
        fileUrl={currentFile.url}
        fileName={currentFile.name}
        fileType={currentFile.type}
      />

      <div style={styles.whiteBackdrop} aria-hidden="true"></div>

      <div className="auth-page-content position-relative" style={{ ...styles.wrapper, zIndex: 1 }}>
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} sm={11} md={10} lg={8} xl={7}>
              {/* Logo */}
              <div className="d-flex justify-content-center align-items-center gap-3 mb-3 mt-2">
                {esMercantil ? (
                  <img src={logo_mercantil} alt="Seguros Mercantil" height="45" />
                ) : (
                  <img src={logo_farmabien} alt="Farme" height="45" />
                )}
              </div>

              <Card className="overflow-hidden" style={styles.card}>
                {facturacionQR ? (
                  <CardBody className="p-3 p-md-4">
                    {/* ── HERO: Verificación Exitosa ── */}
                    <div className="text-center mb-3">
                      
                      
                      <p className="text-muted mb-1" style={{ fontSize: "0.9rem" }}>
                        Tu solicitud ha sido validada correctamente
                      </p>
                      <p className="mb-0" style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                        Hola, <span className="fw-semibold" style={{ color: "#374151" }}>{facturacionQR.persona}</span> 👋
                      </p>
                    </div>

                    {/* CTA primario */}
                    {adjuntoUrl && (
                      <Button
                        color="light"
                        className="rounded-pill w-100 fw-medium mb-3 border"
                        onClick={openAdjunto}
                        style={{ minHeight: 40, fontSize: "0.85rem", color: "#4b5563" }}
                      >
                        <i className="ri-file-text-line me-2 text-secondary"></i> Ver Factura
                      </Button>
                    )}



                    {/* ── MÓDULO DE FEEDBACK (no-MyH+) ── */}
                    {feedbackEnabled && (
                      <div className="mb-3 p-3 rounded-3" style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                        {feedbackAlreadySent ? (
                          <div className="text-center py-2">
                            <i className="ri-checkbox-circle-line text-success d-block mb-1" style={{ fontSize: "1.8rem" }}></i>
                            <h6 className="mb-1">¡Gracias por tu feedback!</h6>
                            <p className="text-muted small mb-0">Tu opinión nos ayuda a mejorar.</p>
                          </div>
                        ) : (
                          <>
                            <h6 className="mb-1 fw-semibold text-center">
                              {rating === null && "¿Cómo fue tu experiencia?"}
                              {rating !== null && rating >= 4 && "¡Gracias por tu valoración!"}
                              {rating !== null && rating <= 3 && "Sentimos escuchar esto"}
                            </h6>
                            <p className="text-muted text-center mb-2" style={{ fontSize: "0.75rem" }}>
                              {rating === null && "Tu opinión nos ayuda a mejorar el servicio."}
                              {rating !== null && rating >= 4 && "¿Qué fue lo que más te gustó? (opcional)"}
                              {rating !== null && rating <= 3 && "Cuéntanos qué podemos mejorar."}
                            </p>

                            {/* Selector de estrellas */}
                            <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
                              {[1, 2, 3, 4, 5].map((star) => {
                                const active = rating !== null && star <= rating;
                                return (
                                  <button
                                    type="button"
                                    key={star}
                                    onClick={() => setRating(star)}
                                    aria-label={`${star} estrellas`}
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      padding: 4,
                                      cursor: "pointer",
                                      lineHeight: 1,
                                    }}
                                  >
                                    <i
                                      className={active ? "ri-star-fill" : "ri-star-line"}
                                      style={{
                                        fontSize: "1.9rem",
                                        color: active ? "#ebc805" : "#93a0b1",
                                        transition: "color 0.15s",
                                      }}
                                    ></i>
                                  </button>
                                );
                              })}
                            </div>

                            {/* Quick tags solo si rating bajo */}
                            {rating !== null && rating <= 3 && (
                              <div className="mb-2 d-flex flex-wrap gap-2 justify-content-center">
                                {FEEDBACK_TAGS.map((tag) => {
                                  const active = feedbackTags.includes(tag);
                                  return (
                                    <button
                                      type="button"
                                      key={tag}
                                      onClick={() => toggleTag(tag)}
                                      className="rounded-pill px-2 py-1"
                                      style={{
                                        fontSize: "0.75rem",
                                        border: active ? "1px solid #ef4444" : "1px solid #d1d5db",
                                        background: active ? "#fef2f2" : "#ffffff",
                                        color: active ? "#ef4444" : "#4b5563",
                                        fontWeight: 500,
                                        cursor: "pointer",
                                        transition: "all 0.15s",
                                      }}
                                    >
                                      {active && <i className="ri-check-line me-1" style={{ fontSize: "0.7rem" }}></i>}
                                      {tag}
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                            {/* Comentario libre (cuando hay rating) */}
                            {rating !== null && (
                              <>
                                <Input
                                  type="textarea"
                                  rows={2}
                                  value={feedbackComment}
                                  onChange={(e) => setFeedbackComment(e.target.value)}
                                  placeholder={rating >= 4 ? "¿Qué te gustó más? (opcional)" : "Cuéntanos los detalles..."}
                                  maxLength={500}
                                  className="mb-2"
                                  style={{ fontSize: "0.85rem" }}
                                />
                                <Button
                                  color="primary"
                                  className="w-100 rounded-pill fw-semibold"
                                  onClick={submitFeedback}
                                  disabled={submittingFeedback}
                                >
                                  {submittingFeedback ? (
                                    <><Spinner size="sm" className="me-1" /> Enviando...</>
                                  ) : (
                                    <><i className="ri-send-plane-line me-1"></i> Enviar feedback</>
                                  )}
                                </Button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    

                    {/* ── TARJETA RECIBO: Detalle del Servicio ── */}
                    <div style={styles.receiptCard} className="mb-3">
                      <div style={styles.receiptHeader}>
                        <span style={styles.receiptHeaderLabel}>Detalle del Servicio</span>
                        <span style={styles.verifiedBadge}>ENTREGADO</span>
                      </div>
                      <div style={styles.receiptRow}>
                        <span style={styles.receiptLabel}>Farmacia</span>
                        <span style={styles.receiptValue}>{nombreFarmaciaNormalizado}</span>
                      </div>
                      <div style={styles.receiptRow}>
                        <span style={styles.receiptLabel}>Tipo</span>
                        <span style={styles.receiptValue}>{facturacionQR.tipo}</span>
                      </div>
                      <div style={styles.receiptRow}>
                        <span style={styles.receiptLabel}>Patología</span>
                        <span style={styles.receiptValue}>{facturacionQR.patologia}</span>
                      </div>
                      <div style={styles.receiptRow}>
                        <span style={styles.receiptLabel}>Fecha</span>
                        <span style={styles.receiptValue}>{handleValidDate(facturacionQR.detalles.fecha)}</span>
                      </div>
                      <div style={styles.receiptRowLast}>
                        <span style={styles.receiptLabel}>N° Factura</span>
                        <span style={styles.receiptValue}>{facturacionQR.detalles.numeroFactura}</span>
                      </div>
                    </div>

                    

                    {/* ── PILLS DE ACCIONES SECUNDARIAS ── 
                    {!hideSecondaryPills && (
                      <div className="d-flex flex-column gap-2 mt-2">
                        <a
                          href="https://gemini.google.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary rounded-pill fw-semibold"
                          style={{ minHeight: 44 }}
                        >
                          <i className="ri-robot-line me-2"></i> Consejos de IA
                        </a>
                        <a
                          href="https://play.google.com/store/apps/details?id=br.com.caiocrol.alarmandpillreminder&pli=1"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary rounded-pill fw-semibold"
                          style={{ minHeight: 44 }}
                        >
                          <i className="ri-notification-3-line me-2"></i> Recordatorios
                        </a>
                      </div>
                    )}*/}
                  </CardBody>
                ) : (
                  <>
                    {/* ── VERIFICACIÓN FALLIDA ── */}
                    <div style={styles.headerError} className="text-center">
                      <div style={{ ...styles.iconCircle, backgroundColor: "rgba(255,255,255,0.2)" }} className="mb-2 mx-auto">
                        <i className="ri-error-warning-line text-white" style={{ fontSize: "1.8rem" }}></i>
                      </div>
                      <h5 className="text-white fw-semibold mb-1">Verificación Fallida</h5>
                      <p className="text-white-50 small mb-0">No pudimos verificar la información</p>
                    </div>

                    <CardBody className="p-3 p-md-4">
                      <div className="text-center py-4">
                        <div className="mb-3">
                          <div style={{ ...styles.iconCircle, width: 80, height: 80, backgroundColor: "#fef2f2" }} className="mx-auto">
                            <i className="ri-file-warning-line text-danger" style={{ fontSize: "2.5rem" }}></i>
                          </div>
                        </div>
                        <h5 className="mb-2">No se encontraron datos válidos</h5>
                        <p className="text-muted small mb-4" style={{ maxWidth: 360, margin: "0 auto" }}>
                          Lo sentimos, no pudimos verificar la información proporcionada. Asegúrese de que el código QR escaneado es correcto.
                        </p>
                        <div className="rounded-3 p-3 text-start" style={{ backgroundColor: "#fef3c7", maxWidth: 360, margin: "0 auto" }}>
                          <p className="small mb-0">
                            <i className="ri-customer-service-2-line me-1 text-warning"></i>
                            <strong>¿Necesita ayuda?</strong> Contacte a nuestro servicio al cliente para asistencia.
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </>
                )}
              </Card>

              {/* Footer especial para Seguros Mercantil */}
              {esMercantil && (
                <div style={styles.mercantilFooter}>
                  <span>Con tecnología</span>
                  <img src={logo_farmabien} alt="Farme" height="30" />
                </div>
              )}

              {/* Footer */}
              <div style={styles.footer}>
                <p className="mb-0"></p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default VerificarFactura;
